import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import { personService } from '@shared/services/personService';
import { notifySuccess, notifyError } from '@config/tostifyConfig/tostifyConfig';

export const usePhysicianForm = (person) => {
    const { t } = useTranslation();
    const [physicianData, setPhysicianData] = useState([]);
    const [isEditing, setIsEditing] = useState(false);
    const [editingPhysician, setEditingPhysician] = useState(null);

    const physicianValidationSchema = yup.object({
        inForceFrom: yup.mixed()
            .test('is-required', t('validation.person.physician.dateRequired'), function(value) {
                return value !== '' && value !== null && value !== undefined;
            })
            .test('not-future-date', t('validation.person.physician.dateNotFuture'), function(value) {
                if (!value) return true;
                const fromDate = new Date(value);
                const today = new Date();
                return fromDate <= today;
            })
            .nullable(),
    });

    const {
        register: physicianFormRegister,
        formState: { errors: physicianErrors },
        setValue: setPhysicianValue,
        handleSubmit,
        reset,
        watch,
        setValue,
        errors
    } = useForm({
        resolver: yupResolver(physicianValidationSchema),
        defaultValues: {
            inForceFrom: '',
            physicianFirstName: '',
            physicianMiddleName: '',
            physicianLastName: '',
            physicianName: '',
            addressOfPractice: '',
            physicianPhone: ''
        }
    });

    const fetchPhysicians = async () => {
        try {
            if (person?.id) {
                const data = await personService.getPhysicians(person.id);
                setPhysicianData(data || []);
            }
        } catch (error) {   
            console.error('Error fetching physicians:', error);
            notifyError(t('persons.physician.fetchError'));
            setPhysicianData([]);
        }
    };

    useEffect(() => {
        fetchPhysicians();
    }, [person?.id]);

    const submitPhysicianForm = async (formData) => {
        try {
            if (!person?.id) {
                notifyError(t('persons.physician.personIdRequired'));
                return false;
            }

            const combinedName = `${formData.physicianFirstName} ${formData.physicianMiddleName} ${formData.physicianLastName}`.trim();

            const payload = {
                personId: person.id,
                effectiveDate: formData.inForceFrom,
                jpName: combinedName,
                jpAddress: formData.addressOfPractice,
                jpPhone: formData.physicianPhone
            };

            if (isEditing && editingPhysician) {
                await personService.updatePhysician(person.id, editingPhysician.id, payload);
                notifySuccess(t('persons.physician.updateSuccess'));
            } else {
                await personService.addPhysician(person.id, payload);
                notifySuccess(t('persons.physician.addSuccess'));
            }

            reset({
                inForceFrom: '',
                physicianFirstName: '',
                physicianMiddleName: '',
                physicianLastName: '',
                physicianName: '',
                addressOfPractice: '',
                physicianPhone: ''
            });
            setIsEditing(false);
            setEditingPhysician(null);
            await fetchPhysicians();
            return true;
        } catch (error) {
            console.error('Physician operation failed:', error);
            notifyError(isEditing 
                ? t('persons.physician.updateError')
                : t('persons.physician.addError')
            );
            return false;
        }
    };

    const handleEdit = (physician) => {
        setIsEditing(true);
        setEditingPhysician(physician);
        
        const nameParts = physician.physicianName?.split(' ') || [];
        
        setPhysicianValue('inForceFrom', physician.inForceFrom || '');
        setPhysicianValue('physicianFirstName', nameParts[0] || '');
        setPhysicianValue('physicianMiddleName', nameParts[1] || '');
        setPhysicianValue('physicianLastName', nameParts[2] || '');
        setPhysicianValue('physicianName', physician.physicianName || '');
        setPhysicianValue('addressOfPractice', physician.addressOfPractice || '');
        setPhysicianValue('physicianPhone', physician.physicianPhone || '');
    };

    const handleDelete = async (physicianId) => {
        try {
            await personService.deletePhysician(physicianId); // Remove person.id parameter
            notifySuccess(t('persons.physician.deleteSuccess'));
            await fetchPhysicians(); // Refresh the list after deletion
        } catch (error) {
            console.error('Delete error:', error);
            notifyError(t('persons.physician.deleteError'));
        }
    };

    const handleCancel = () => {
        reset({
            inForceFrom: '',
            physicianFirstName: '',
            physicianMiddleName: '',
            physicianLastName: '',
            physicianName: '',
            addressOfPractice: '',
            physicianPhone: ''
        });
        setIsEditing(false);
        setEditingPhysician(null);
    };

    return {
        physicianData,
        physicianFormRegister,
        physicianErrors,
        handleEdit,
        handleDelete,
        submitPhysicianForm,
        isEditing,
        handleSubmit,
        handleCancel,
        watch,
        setValue,
        errors
    };
}; 